import { useEffect, useState, useMemo } from "react"
import { keyBy, mapValues, get, sortBy } from "lodash"
import type { ProductCardItem } from "@gatsby-plugin-marketplace/fragments/marketplaceProductCard"
import useAllMarketplaceProductsQuery from "~/hooks/useAllMarketplaceProductsQuery"
import LocalStorage from "~/lib/storage/LocalStorage"

const VISITED_PRODUCTS_KEY = "visitedProducts"

export default function useLastViewedProducts(
  url: string,
  limit: number = 5
): ProductCardItem[] {
  const all = useAllMarketplaceProductsQuery()
  const [lastViewedProducts, setLastViewedProducts] = useState<
    ProductCardItem[]
  >([])

  const urls = useMemo(
    () =>
      mapValues(
        keyBy(all, (item: ProductCardItem) =>
          get(item, "flatData.pages.0.flatData.url", "")
        ),
        (item: ProductCardItem): string =>
          item.flatData.saleConfigurations[0]?.flatData.defaultCloudBluePlan[0]
            ?.flatData.alias || ""
      ),
    [all]
  )

  useEffect(() => {
    const lastViewedProductsFromStorage = (
      LocalStorage.getArray(VISITED_PRODUCTS_KEY) || []
    )
      .filter((element) => urls[element])
      .slice(0, 10)

    LocalStorage.setArray(
      VISITED_PRODUCTS_KEY,
      Array.from(new Set([url, ...lastViewedProductsFromStorage])).slice(0, 6)
    )

    const currentLastViewedProducts = sortBy(
      all.filter(
        (item: ProductCardItem): boolean =>
          lastViewedProductsFromStorage.includes(
            get(item, "flatData.pages.0.flatData.url", "")
          ) && get(item, "pages.0.url") !== url
      ),
      (item: ProductCardItem) =>
        lastViewedProductsFromStorage.indexOf(
          get(item, "flatData.pages.0.flatData.url", "")
        )
    )

    setLastViewedProducts(currentLastViewedProducts.slice(0, limit))
  }, [all, url, urls])

  return lastViewedProducts
}
